import { useState, useEffect, useCallback } from 'react'
import { useParams } from 'react-router'
import useRouter from 'utils/useRouter'
import tiposExames from 'globals/tiposDeExame'
import { useExameRealizadoService } from 'hooks/useExameRealizadoService'
import { useExameIdentificacaoCadavericaService } from 'hooks/useExameIdentificacaoCadavericaService'

export const useExameIdentificacaoCadaverica = () => {
  const params = useParams()
  const router = useRouter()
  const exameRealizadoService = useExameRealizadoService()
  const exameIdentificacaoService = useExameIdentificacaoCadavericaService()

  const [examesDoCaso, setExamesDoCaso] = useState([])
  const [exameIdentificacao, setExameIdentificacao] = useState({})
  const [processandoExame, setProcessandoExame] = useState(false)
  const [processandoConfirmacao, setProcessandoConfirmacao] = useState(false)
  const [arrayFiliacao, setArrayFiliacao] = useState(['filiacao0', 'filiacao1'])

  // Form State
  const [formState, setFormState] = useState({
    values: {},
    touched: {},
    errors: {},
  })

  // Fetch Exame de Identificação Cadavérica on mount
  const fetchExameIdentificacaoCadaverica = useCallback(async () => {
    try {
      const response = await exameIdentificacaoService.listarExameIdentificacaoCadavericaAsync(
        params.id,
        setProcessandoExame
      )
      setExameIdentificacao(response)
    } catch (error) {
      console.error('Failed to fetch cadaveric identification exam details', error)
    }
  }, [params.id])

  useEffect(() => {
    fetchExameIdentificacaoCadaverica()
  }, [fetchExameIdentificacaoCadaverica])

  // Fetch Exames Realizados do Caso when id_caso is available
  const fetchExamesRealizadosDeCaso = useCallback(async (idCaso) => {
    try {
      const response = await exameRealizadoService.listarExamesRealizadosDeCasoPorTiposAsync(idCaso, [
        tiposExames.NECROPAPILOSCOPIA,
        tiposExames.DNA,
        tiposExames.ANTROPOLOGICO,
        tiposExames.IDENTIFICACAO_ODONTO,
      ])
      setExamesDoCaso(response)
    } catch (error) {
      console.error('Failed to fetch case exams', error)
    }
  }, [])

  useEffect(() => {
    if (exameIdentificacao?.exReal?.exSol?.solEx?.id_caso) {
      fetchExamesRealizadosDeCaso(exameIdentificacao.exReal.exSol.solEx.id_caso)
    }
  }, [exameIdentificacao, fetchExamesRealizadosDeCaso])

  // Populate form values from fetched exam
  useEffect(() => {
    if (exameIdentificacao && exameIdentificacao.id) {
      if (exameIdentificacao.filiacoes && exameIdentificacao.filiacoes.length > 0) {
        let objectFiliacoes = {}
        let arrayFiliacoesDocumento = []
        exameIdentificacao.filiacoes.forEach((filiacao, index) => {
          objectFiliacoes = {
            ...objectFiliacoes,
            [`filiacao${index}`]: filiacao,
          }
          arrayFiliacoesDocumento.push(`filiacao${index}`)
        })
        setArrayFiliacao(arrayFiliacoesDocumento)
        setFormState((prevState) => ({
          ...prevState,
          values: {
            ...prevState.values,
            ...exameIdentificacao,
            ...objectFiliacoes,
          },
        }))
      } else {
        setFormState((prevState) => ({
          ...prevState,
          values: {
            ...prevState.values,
            ...exameIdentificacao,
          },
        }))
        setArrayFiliacao(['filiacao0', 'filiacao1'])
      }
    }
  }, [exameIdentificacao])

  // Form State Handlers
  const handleChange = useCallback((event) => {
    event.persist?.()
    const { name, value, type, checked } = event.target
    setFormState((prevState) => ({
      ...prevState,
      values: {
        ...prevState.values,
        [name]: type === 'checkbox' ? checked : value,
      },
      touched: {
        ...prevState.touched,
        [name]: true,
      },
    }))
  }, [])

  const handleChangeNewValueByNameProperty = useCallback((event, newValue, property) => {
    setFormState((prevState) => ({
      ...prevState,
      values: {
        ...prevState.values,
        [property]: newValue,
      },
      touched: {
        ...prevState.touched,
        [property]: true,
      },
    }))
  }, [])

  const hasError = useCallback((field) => {
    return !!(formState.touched[field] && formState.errors[field])
  }, [formState.touched, formState.errors])

  const form = {
    formState,
    setFormState,
    handleChange,
    handleChangeNewValueByNameProperty,
    hasError,
  }

  // Filiacoes dynamic list
  const handleAdicionarFiliacao = useCallback(() => {
    setArrayFiliacao((prev) => {
      const newInput = `filiacao${prev.length}`
      return [...prev, newInput]
    })
  }, [])

  // Date Parsing and Formatting safely (Local timezone without timestamp)
  const parseDataLocal = useCallback((dateString) => {
    if (!dateString) return null
    if (dateString instanceof Date) return dateString
    const parts = String(dateString).split('-')
    if (parts.length === 3) {
      const [ano, mes, dia] = parts
      return new Date(parseInt(ano), parseInt(mes) - 1, parseInt(dia))
    }
    const partsSlash = String(dateString).split('/')
    if (partsSlash.length === 3) {
      const [dia, mes, ano] = partsSlash
      return new Date(parseInt(ano), parseInt(mes) - 1, parseInt(dia))
    }
    return new Date(dateString)
  }, [])

  const handleDateChange = useCallback((date) => {
    if (!date || isNaN(Date.parse(date))) {
      setFormState((prevState) => ({
        ...prevState,
        values: {
          ...prevState.values,
          dataNascimento: null,
        },
      }))
      return
    }
    const d = new Date(date)
    const ano = d.getFullYear()
    const mes = String(d.getMonth() + 1).padStart(2, '0')
    const dia = String(d.getDate()).padStart(2, '0')

    setFormState((prevState) => ({
      ...prevState,
      values: {
        ...prevState.values,
        dataNascimento: `${ano}-${mes}-${dia}`,
      },
    }))
  }, [])

  // Navigation handlers
  const handleVoltar = useCallback(() => {
    router.history.goBack()
  }, [router.history])

  const handleConfirmar = useCallback(async () => {
    setProcessandoConfirmacao(true)
    const filiacoes = []
    Object.keys(formState.values).forEach((key) => {
      if (key.indexOf('filiacao') !== -1) {
        filiacoes.push(formState.values[key])
      }
    })
    const dados = {
      ...formState.values,
      id_exame_base: formState.values.exBase ? formState.values.exBase.id : null,
      filiacoes,
    }
    try {
      await exameIdentificacaoService.editarExameIdentificacaoCadaverica(params.id, dados)
      await exameRealizadoService.gerarDocumento(params.id)
      setProcessandoConfirmacao(false)
      router.history.push({
        pathname: `/casos/caso/solicitacao/exameSolicitado/editorExameRealizado/${params.id}`,
        state: { IgnoneUploadImagem: true },
      })
    } catch (error) {
      setProcessandoConfirmacao(false)
      console.error('Failed to save cadaveric identification exam', error)
    }
  }, [formState.values, params.id, router.history])

  return {
    examesDoCaso,
    processandoExame,
    processandoConfirmacao,
    arrayFiliacao,
    form,
    handleAdicionarFiliacao,
    parseDataLocal,
    handleDateChange,
    handleVoltar,
    handleConfirmar,
  }
}
